export interface LanguageOption {
  code: string;
  baseCode: string;
  display: string;
  regionLabel?: string;
  regions: "global" | readonly string[];
}

const ENGLISH_GB_REGIONS = new Set<string>([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "GB",
  "UK",
  "AE",
  "BH",
  "EG",
  "IL",
  "IQ",
  "JO",
  "KW",
  "LB",
  "OM",
  "PS",
  "QA",
  "SA",
  "SY",
  "YE",
  "ZA",
  "AU",
  "NZ",
] as const);

const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
  {
    code: "en",
    baseCode: "en",
    display: "English",
    regionLabel: "United States",
    regions: "global",
  },
  {
    code: "en-GB",
    baseCode: "en",
    display: "English",
    regionLabel: "United Kingdom",
    regions: Object.freeze([...ENGLISH_GB_REGIONS]),
  },
  {
    code: "fr",
    baseCode: "fr",
    display: "French",
    regionLabel: "France",
    regions: "global",
  },
  {
    code: "fr-CA",
    baseCode: "fr",
    display: "French",
    regionLabel: "Canada",
    regions: ["CA"],
  },
  {
    code: "ar",
    baseCode: "ar",
    display: "Arabic",
    regionLabel: "Middle East",
    regions: "global",
  },
] as const;

export const SUPPORTED_LANGUAGE_CODES = LANGUAGE_OPTIONS.map((option) => option.code);
export const USER_LANGUAGE_STORAGE_KEY = "dashboard-language-preference";
const RTL_BASE_LANGUAGE_CODES = new Set(["ar"]);

const parseLocaleParts = (locale?: string) => {
  if (!locale) {
    return { language: "", region: "" };
  }

  const normalized = locale.replace("_", "-");
  const [language = "", scriptOrRegion = "", region = ""] = normalized.split("-");
  const upperSecondPart = scriptOrRegion.toUpperCase();

  if (scriptOrRegion.length === 2 || scriptOrRegion.length === 3 || upperSecondPart === "419") {
    return {
      language: language.toLowerCase(),
      region: upperSecondPart,
    };
  }

  return {
    language: language.toLowerCase(),
    region: region.toUpperCase(),
  };
};

export const getPreferredEnglishLanguage = (locale?: string): "en" | "en-GB" => {
  const { region } = parseLocaleParts(locale);
  return ENGLISH_GB_REGIONS.has(region) ? "en-GB" : "en";
};

export const resolvePreferredLanguage = (locale?: string): string => {
  const { language, region } = parseLocaleParts(locale);

  if (!language) {
    return "en";
  }

  if (language === "en") {
    return getPreferredEnglishLanguage(locale);
  }

  if (language === "fr") {
    return region === "CA" ? "fr-CA" : "fr";
  }

  const exactMatch = SUPPORTED_LANGUAGE_CODES.find(
    (code) => code.toLowerCase() === locale?.toLowerCase(),
  );
  if (exactMatch) {
    return exactMatch;
  }

  return SUPPORTED_LANGUAGE_CODES.includes(language) ? language : "en";
};

export const resolveDetectedLanguage = (
  detectedLanguage?: string,
  browserLocale?: string,
): string => {
  const resolvedLanguage = resolvePreferredLanguage(detectedLanguage);

  if (resolvedLanguage !== "en") {
    return resolvedLanguage;
  }

  return getPreferredEnglishLanguage(browserLocale);
};

export const getFallbackLanguages = (locale?: string, browserLocale?: string): string[] => {
  const resolvedLanguage = resolvePreferredLanguage(locale ?? browserLocale);
  const localeWithRegion = parseLocaleParts(locale).region ? locale : browserLocale;
  const preferredEnglish = getPreferredEnglishLanguage(localeWithRegion);

  return Array.from(new Set([resolvedLanguage, preferredEnglish, "en"]));
};

export const getAvailableLanguagesForLocale = (locale?: string): LanguageOption[] => {
  const { region } = parseLocaleParts(locale);

  return LANGUAGE_OPTIONS.filter((option) => {
    if (option.regions === "global") {
      return !LANGUAGE_OPTIONS.some(
        (variant) =>
          variant.baseCode === option.baseCode &&
          variant.code !== option.code &&
          variant.regions !== "global" &&
          variant.regions.includes(region),
      );
    }

    return option.regions.includes(region);
  });
};

export const getLanguageOption = (code?: string): LanguageOption | undefined =>
  LANGUAGE_OPTIONS.find((option) => option.code === code);

export const isRTLLanguage = (code?: string): boolean => {
  const { language } = parseLocaleParts(code);
  return RTL_BASE_LANGUAGE_CODES.has(language);
};
